import { AfterViewInit, Component, Input, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { Subject, of } from 'rxjs';
import { finalize, mergeMap, takeUntil } from 'rxjs/operators';
import { EGIS_SERVICE_CALLER } from 'src/app/constants';
import { LoaderService } from 'src/app/services/loader.service';
import { MatTableDataSource } from '@angular/material/table';
import { AfregningService } from 'src/app/api/services/afregning.service';
import { Router } from '@angular/router';
import { KreditnotaDialogComponent } from 'src/app/components/kreditnota/kreditnota-dialog/kreditnota-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { KreditnotaService } from 'src/app/api/services/kreditnota.service';
import { KreditnotaDialogData, KreditnotaDialogResponse } from '../../models';
import { SignalrService } from 'src/app/services/signalr.service';
import { AuthService } from 'src/app/services/auth.service';
import { AfregningsType } from '../models/selectfiltermodels';
import { FilterOption } from 'src/app/components/shared/history/select-filter/select-filter.component';
import { AfregningDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/afregning-dto';
import { OpkraevningScopeEnum } from 'src/app/api/models/OK/Egis/Service/Models/Entities/Enums/opkraevning-scope-enum';
import { ServiceaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/serviceaftale-dto';
import { CreateFullKreditNotaResponseDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/create-full-kredit-nota-response-dto';
import { CreateFullKreditNotaRequestDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/create-full-kredit-nota-request-dto';
import { KreditvurderingService } from 'src/app/api/services/kreditvurdering.service';
import { KreditvurderingDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvurdering-dto';
import { KreditvaerdigEnumDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/kreditvaerdig-enum-dto';
import { KreditvurderingDialogComponent } from 'src/app/component/ordre/kreditvurdering-dialog/kreditvurdering-dialog.component';
import { CreateServiceOrdreComponentDialogData } from 'src/app/components/aftale/models';
@Component({
  selector: 'app-opkraevninger',
  templateUrl: './opkraevninger.component.html',
  styleUrls: ['./opkraevninger.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class OpkraevningerComponent implements OnInit, AfterViewInit {
  columns: string[] = [
    'opkraevningDate',
    'overfoertDate',
    'periode',
    'type',
    'bilagsnr',
    'beloebExclMoms',
    'serviceordreNo',
    'opkraevningScope',
    'kreditnota',
  ];
  dataSource: any = [];

  afregninger: AfregningDto[];
  filteredAfregninger: AfregningDto[] = [];
  showPartnerCredit: boolean = true;
  isAdmin: boolean = false;
  isSuperUser: boolean = false;
  canKreditnota: boolean = false;
  opkraevningScopePartner = OpkraevningScopeEnum.Partner;
  kreditvurdering: KreditvurderingDto;

  _selectedAfregningType: AfregningsType = AfregningsType.Alle;
  set selectedAfregningType(value: AfregningsType) {
    this._selectedAfregningType = value;
    this.filterAfregningTypes(this._selectedAfregningType);
  }

  @Input() serviceaftale: ServiceaftaleDto;

  unsubscribe$ = new Subject<void>();

  constructor(
    private afregningService: AfregningService,
    private loader: LoaderService,
    private errorHandler: ErrorHandlerService,
    private router: Router,
    private dialog: MatDialog,
    private kreditnotaService: KreditnotaService,
    private signalR: SignalrService,
    private auth: AuthService,
    private kreditvurderingService: KreditvurderingService,
  ) {
    this.dataSource = new MatTableDataSource(this.filteredAfregninger);
    this.isAdmin = this.auth.isAdministrator;
    this.isSuperUser = this.auth.isSuperuser;
    this.canKreditnota = this.auth.isAdministrator || this.auth.isSuperuser;

    this._selectedAfregningType = this.isAdmin ? AfregningsType.Alle : AfregningsType.Kundeopkrævninger;
  }

  afregningsTypesKeys = Object.keys(AfregningsType)
    .filter(Number)
    .map(([id]) => new FilterOption(parseInt(id), AfregningsType[id], parseInt(id) === AfregningsType.Alle));

  @ViewChild(MatSort) sort: MatSort;

  ngOnInit(): void {
    this.getData();
    this.signalR.serviceafregningCreated$.pipe(takeUntil(this.unsubscribe$)).subscribe((afregning: AfregningDto) => {
      if (afregning.serviceaftaleId == this.serviceaftale.id) {
        this.getData();
      }
    });
  }

  onRowClick(afregning: AfregningDto) {
    if (afregning.serviceordreNo != null) {
      this.router.navigate(['/ordre', afregning.serviceordreNo, 'edit']);
    }
  }

  filterAfregningTypes(afregningsType: AfregningsType) {
    if (afregningsType == AfregningsType.Partnerafregninger) {
      this.filteredAfregninger = this.afregninger.filter((x) => x.opkraevningScope == OpkraevningScopeEnum.Partner);
    } else if (afregningsType == AfregningsType.Kundeopkrævninger) {
      this.filteredAfregninger = this.afregninger.filter((x) => x.opkraevningScope == OpkraevningScopeEnum.Customer);
    } else {
      this.filteredAfregninger = this.afregninger;
    }
    this.dataSource = new MatTableDataSource(this.filteredAfregninger);
    this.dataSource.sort = this.sort;
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  getData() {
    this.loader.show();

    this.afregningService
      .getAfregningerForServiceaftale({
        caller: EGIS_SERVICE_CALLER,
        serviceaftaleId: this.serviceaftale.id,
      })
      .pipe(
        finalize(() => {
          this.loader.hide();
        }),
        takeUntil(this.unsubscribe$),
      )
      .subscribe(
        (result: AfregningDto[]) => {
          this.afregninger = result;
          this.filterAfregningTypes(this._selectedAfregningType);
        },
        (error) => {
          this.errorHandler.HandleError(error);
        },
      );
  }

  doCredit(afregning: AfregningDto) {
    let harPartnerTilbagefoersel = !!afregning.partnerTilbagefoersel;
    let harPartnerAfregning = !!afregning.partnerAfregning;
    let path = '';
    const data: KreditnotaDialogData = {
      afregning: afregning,
      serviceaftale: this.serviceaftale,
      harPartnerTilbagefoersel: harPartnerTilbagefoersel,
      harPartnerAfregning: harPartnerAfregning,
    };
    let dialogRef = this.dialog.open(KreditnotaDialogComponent, {
      width: '600px',
      maxWidth: '95vw',
      data: data,
    });

    dialogRef
      .afterClosed()
      .pipe(
        mergeMap((response: KreditnotaDialogResponse) => {
          if (!response) {
            return of(null);
          }
          afregning.hasKreditering = true;
          this.loader.show();
          if (response.nyFaktura && afregning.serviceordreNo) {
            path = `/ordre/${afregning.serviceordreNo}/edit`;
          }
          const request: CreateFullKreditNotaRequestDto = {
            meddelelsestekst: response.comment,
            serviceafregningId: afregning.serviceafregningId,
            serviceaftaleId: afregning.serviceaftaleId,
            nyFaktura: response.nyFaktura,
            partnerkorrektion: response.partnerkorrektion,
          };
          return this.kreditnotaService.createFullKreditnota({ caller: EGIS_SERVICE_CALLER, body: request });
        }),
      )
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loader.hide();
        }),
      )
      .subscribe(
        (response: CreateFullKreditNotaResponseDto) => {
          this.afregninger = response.afregningDtos;
          this.filteredAfregninger = response.afregningDtos;
          // hvis ny faktura er valgt, navigeres til ordren for at kunne rettet+godkende igen
          if (path) setTimeout(() => this.router.navigateByUrl(path), 100);
        },
        (err) => {
          afregning.hasKreditering = false;
          this.errorHandler.HandleError(err);
        },
      );
  }

  async kreditnota(event: Event, afregning: AfregningDto) {
    event.stopPropagation();

    this.loader.show();
    await this.kreditvurderingService
      .getKreditVurderingForServiceaftale({
        caller: EGIS_SERVICE_CALLER,
        okKundeAftaleNr: this.serviceaftale.okKundeAftaleNr,
      })
      .toPromise()
      .then(async (response) => {
        this.kreditvurdering = response;
      })
      .catch((error) => {
        this.errorHandler.HandleError(error);
      })
      .finally(() => {
        this.loader.hide();
      });

    if (this.kreditvurdering.kreditvaerdig === KreditvaerdigEnumDto.Yes || afregning.opkraevningScope === this.opkraevningScopePartner) {
      this.doCredit(afregning);
    } else {
      const data: CreateServiceOrdreComponentDialogData = {
        serviceaftale: this.serviceaftale,
        kreditvurdering: this.kreditvurdering,
        opgavetype: null,
        buttonText: 'Opret kreditnota',
      };
      const dialogKreditvurderingRef = this.dialog.open(KreditvurderingDialogComponent, {
        data: data,
        disableClose: true,
        hasBackdrop: true,
      });
      dialogKreditvurderingRef.afterClosed().subscribe((result) => {
        if (result) {
          this.doCredit(afregning);
        }
      });
    }
  }
}
